import * as React from 'react';
import {useRef} from 'react';

import {view, createViewConfig, dynamic, assignProps} from '@sail/engine';
import type {View} from '@sail/engine';
import {checkCircleFilled} from '@sail/icons/react/Icon';
import type {IconAsset, IconCategory} from '@sail/icons/types';

import type {ItemTransforms, TreeState} from '@sail/react-aria';
import {
  isFocusVisible,
  useCollectionState,
  useItem,
  useOption,
} from '@sail/react-aria';
import {NativeCheckbox} from './Checkbox';
import {Icon, IconConfig} from './Icon';
import {css} from '../css';
import {
  CollectionGroupContext,
  withCollectionGroup,
} from './CollectionGroupContext';

/**
 * These icon categories are ones that we feel need to be shown
 * slightly larger, due to their low visual weight at 12px
 */
const SPECIAL_ICON_CATEGORIES: IconCategory[] = [
  'flag',
  'brand',
  'card',
  'paymentmethod',
];

export type FauxTreeItemProps = {
  /**
   * Unique identifier, used as the value in `onSelectionChange`
   */
  id: string;
  /**
   * Stops the item from being selectable
   */
  // disabled?: boolean;
  /**
   * The contents of the FauxTree item.
   */
  children?: React.ReactNode;
  /**
   * An optional way to add more context to a list item element. Will be exposed to screenreaders as `aria-describedby`
   */
  description?: React.ReactNode;
  /**
   * An optional way to add an icon to a list item element.
   */
  icon?: React.ReactNode;
  /**
   * If the item contains a non-textual visual representation of the item data (like a country flag)
   * then you must provide a label describing the item for accessibility.
   */
  'aria-label'?: string;
  /**
   * Used for filtering and accessibility features.
   */
  textValue?: string;
  /**
   * Additional data used during filtering or rendering, the lighter the better
   * */
  data?: Record<string, unknown>;

  subviews?: View.Subviews<{
    content: 'span';
    label: 'span';
    description: 'span';
    checked: typeof Icon;
  }>;
};

export const FauxTreeItemTransforms: ItemTransforms<
  FauxTreeItemProps,
  'textValue'
> = {
  defaultField: 'textValue',
  makeGenericItem(props: FauxTreeItemProps) {
    const textValue =
      props.textValue ||
      (typeof props.children === 'string' ? props.children : props.id);
    return {
      'aria-label': props['aria-label'] || textValue,
      textValue,
      data: {icon: props.icon, children: props.children},
    };
  },
};

export const FauxTreeItemConfig = createViewConfig({
  name: 'FauxTreeItem',
  props: {} as FauxTreeItemProps,
});

export const FauxTreeItem = FauxTreeItemConfig.createView(
  (allProps) => {
    const {
      id,
      children,
      textValue,
      subviews,
      icon,
      description,
      // `data` is not used anywhere right now
      data,
      ...props
    } = allProps;
    const collectionGroupKey = React.useContext(CollectionGroupContext);
    const key = useItem(
      id,
      withCollectionGroup(
        FauxTreeItemTransforms.makeGenericItem({
          id,
          'aria-label': props['aria-label'],
          children,
          textValue,
          icon,
        }),
        collectionGroupKey,
      ),
    );
    const state = useCollectionState();
    const tree = state as TreeState;

    const ref = useRef<HTMLDivElement>(null);
    const {
      optionProps,
      isFocused,
      isSelected,
      isDisabled,
      labelProps,
      descriptionProps,
    } = useOption({key}, state, ref);

    return (
      <view.div
        {...props}
        ref={ref}
        uses={[
          assignProps(optionProps as View.IntrinsicElement<'div'>),
          isFocusVisible() && isFocused
            ? css({
                focusRing: 'focus',
                // create new stacking context to allows the focus ring to appear on top
                // of the potential hover state in the item below it, without resorting to
                // bumping the zIndex
                position: 'relative',
              })
            : css({
                // focus ring will only apply when focus-visible and focused
                focusRing: 'none',
              }),
        ]}
      >
        {state.selectionManager.selectionMode === 'multiple' ? (
          <view.div
            css={{
              position: 'relative',
              stack: 'x',
              top: 'xxsmall',
            }}
          >
            <NativeCheckbox
              tabIndex={-1}
              checked={isSelected}
              disabled={isDisabled}
              readOnly
            />
          </view.div>
        ) : null}
        <IconConfig.Provider
          value={{
            uses: [
              // certain categories of icons are too small at 12px, so we bump them up to 16px
              dynamic(({icon}: {icon: IconAsset}) => {
                const result = [];
                if (SPECIAL_ICON_CATEGORIES.includes(icon.category)) {
                  result.push(css({width: 'medium', height: 'medium'}));
                }
                return result;
              }),
              css({
                // irrespective of the icon's size, provision the same space
                flexBasis: '16px',
                position: 'relative',
              }),
              isDisabled ? css({opacity: '30%'}) : css({}),
            ],
          }}
        >
          {icon}

          {!icon &&
          tree.expandable &&
          state.selectionManager.selectionMode !== 'multiple' ? (
            <view.span role="presentation" css={{width: 'medium'}} />
          ) : null}

          {description ? (
            <view.span inherits={subviews.content}>
              <view.span {...labelProps} inherits={subviews.label}>
                {children}
              </view.span>
              <view.span
                {...descriptionProps}
                css={[
                  css({
                    font: 'label.small',
                    color: 'subdued',
                    fontWeight: 'normal',
                  }),
                  isDisabled ? css({color: 'form.disabled'}) : css({}),
                ]}
                inherits={subviews.description}
              >
                {description}
              </view.span>
            </view.span>
          ) : (
            children
          )}
        </IconConfig.Provider>
        {state.selectionManager.selectionMode !== 'multiple' ? (
          <Icon
            icon={checkCircleFilled}
            role="img"
            css={[isSelected ? css({opacity: 1}) : css({opacity: 0})]}
            inherits={subviews.checked}
          />
        ) : null}
      </view.div>
    );
  },
  {
    css: {
      stack: 'x',
      font: 'label.medium',
      color: 'form',
      gap: 'space.75',
      borderRadius: 'xsmall',
      paddingY: 'space.75',
      paddingX: 'space.125',
      alignX: 'start',
      alignY: 'baseline',
      minHeight: 'xlarge',
      cursor: 'pointer',

      ':hover': {
        backgroundColor: 'offset',
      },

      '[aria-disabled="true"]': {
        color: 'form.disabled',
        cursor: 'default',
      },

      '[aria-disabled="true"]:hover': {
        backgroundColor: 'form',
      },
    },

    subviews: {
      checked: {
        css: {
          pointerEvents: 'none',
          transitionProperty: 'opacity',
          transitionDuration: 'medium',
          marginLeft: 'auto',
        },
      },
      content: {
        css: {
          stack: 'y',
          // Prevents selection and cursor changes and event overrides
          pointerEvents: 'none',
        },
      },
    },

    uses: [IconConfig.customize({defaults: {size: 'xsmall'}})],
  },
);
